import { startTransition, useEffect, useRef, useState } from "react";
import MobilePlaceMapMaplibre from "./places-mobile-place-map-maplibre";
import { useSearchParams } from "next/navigation";
import {
  createPlaceSearchHistory,
  getAllPlacesWithReviews,
} from "@/api/places";
import { getZoomForAreaBounds } from "@/utils/map-area";
import { enqueueSnackbar } from "notistack";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { calculateDistanceBetweenCoords } from "@/utils/helpers";
import {
  buildColocationGroups,
  getColocatedCoordKey,
  getSpreadCoordinates,
} from "@/utils/placeMapMarkers";

const getPlaceResultKeys = (place, fallbackId) => {
  const keys = [
    place?._id,
    place?.id,
    place?.placeUniqueNumber,
    place?.contentid,
    place?.contentId,
    place?.publicId,
    fallbackId,
  ];

  return keys.filter((value) => value != null && value !== "").map(String);
};

const getPlaceResultId = (place, fallbackId) => {
  if (place?.source === "api") {
    return (
      place?.placeUniqueNumber ||
      place?.contentid ||
      place?.contentId ||
      place?.id ||
      place?._id ||
      place?.publicId ||
      fallbackId
    );
  }

  return getPlaceResultKeys(place, fallbackId)[0];
};

const normalizePlaceName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const getPlaceCoords = (place) => {
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

const isLikelySamePlace = (a, b) => {
  const aName = normalizePlaceName(a?.name);
  const bName = normalizePlaceName(b?.name);
  if (!aName || aName !== bName) return false;

  const aCoords = getPlaceCoords(a);
  const bCoords = getPlaceCoords(b);
  if (!aCoords || !bCoords) return false;

  return calculateDistanceBetweenCoords(aCoords, bCoords) <= 80;
};

const getPlaceSourcePriority = (place) => (place?.source === "api" ? 1 : 0);

const getPlaceCompletenessScore = (place) => {
  const mediaCount = Array.isArray(place?.placeMedia) ? place.placeMedia.length : 0;
  const reviewCount = Number(
    place?.totalDbReviews || place?.numberOfVisitorReviews || place?.totalReviews || 0
  );
  const hasPhone = place?.phoneNumber || place?.phone ? 1 : 0;
  const hasHours = place?.businessHours || place?.kakaoBusinessHours ? 1 : 0;
  return mediaCount * 10 + reviewCount + hasPhone + hasHours;
};

const shouldPreferPlace = (candidate, current) => {
  const candidatePriority = getPlaceSourcePriority(candidate);
  const currentPriority = getPlaceSourcePriority(current);
  if (candidatePriority !== currentPriority) {
    return candidatePriority < currentPriority;
  }

  return getPlaceCompletenessScore(candidate) > getPlaceCompletenessScore(current);
};

const mergeDuplicatePlace = (current, candidate) => {
  const preferred = shouldPreferPlace(candidate, current) ? candidate : current;
  const fallback = preferred === candidate ? current : candidate;
  const preferredMedia = Array.isArray(preferred?.placeMedia)
    ? preferred.placeMedia
    : [];
  const fallbackMedia = Array.isArray(fallback?.placeMedia)
    ? fallback.placeMedia
    : [];

  return {
    ...fallback,
    ...preferred,
    placeMedia: preferredMedia.length > 0 ? preferredMedia : fallbackMedia,
  };
};

const mergePlacesByIdentifier = (...placeGroups) => {
  const seen = new Map();
  const merged = [];

  placeGroups.flat().forEach((place, index) => {
    if (!place) return;
    const keys = getPlaceResultKeys(place, `place-${index}`);
    const exactIndex = keys
      .map((key) => seen.get(key))
      .find((item) => item !== undefined);
    const duplicateIndex =
      exactIndex ??
      merged.findIndex((existingPlace) => isLikelySamePlace(existingPlace, place));

    if (duplicateIndex >= 0) {
      merged[duplicateIndex] = mergeDuplicatePlace(merged[duplicateIndex], place);
      getPlaceResultKeys(merged[duplicateIndex], `place-${duplicateIndex}`).forEach(
        (key) => seen.set(key, duplicateIndex)
      );
      keys.forEach((key) => seen.set(key, duplicateIndex));
      return;
    }

    const nextIndex = merged.length;
    merged.push(place);
    keys.forEach((key) => seen.set(key, nextIndex));
  });

  return merged;
};

const getPlacesTotalCount = (response) => {
  const totalCount = Number(response?.totalCount);
  if (Number.isFinite(totalCount)) return totalCount;
  return Array.isArray(response?.places) ? response.places.length : 0;
};

const getInitialLandingPlaces = (initialPlacesView) =>
  Array.isArray(initialPlacesView?.places) ? initialPlacesView.places : [];

const MobileSecondaryScreen = ({
  screen,
  switchScreen,
  addQueryParam,
  removeQueryParam,
  currentUserLocation,
  searchQuery,
  setSearchQuery,
  mapCenter,
  setMapCenter,
  mapZoom,
  setMapZoom,
  openPlaceAddModal,
  initialPlacesView = null,
}) => {
  const { user } = useAuthContext();
  const searchParams = useSearchParams();
  const _mz = searchParams.get("mz");
  const _mc = searchParams.get("mc");
  const _mr = searchParams.get("radius");
  const rawSearchPlace = searchParams.get("search-place");
  const initialLandingPlaces = getInitialLandingPlaces(initialPlacesView);
  const initialLandingSearchQuery = initialPlacesView?.searchQuery || "";
  const hasInitialLandingView = Boolean(
    initialLandingSearchQuery || initialLandingPlaces.length
  );
  const _searchPlace = hasInitialLandingView ? null : rawSearchPlace;

  const [mapBounds, setMapBounds] = useState(null);
  const [mapMarkers, setMapMarkers] = useState(() =>
    initialLandingPlaces.length > 0 ? generateMarkers(initialLandingPlaces) : []
  );

  const [totalResults, setTotalResults] = useState(
    initialPlacesView?.totalPlaces || initialLandingPlaces.length || 0
  );
  const [hasMorePlaces, setHasMorePlaces] = useState(true);
  const [placeListLoader, setPlaceListLoader] = useState(
    initialLandingPlaces.length === 0
  );
  const [infiniteScrollLoader, setInfiniteScrollLoader] = useState(false);
  const [listPageNo, setListPageNo] = useState(1);
  const [apiPageNo, setApiPageNo] = useState(0);
  const [totalPlacesAvailable, setTotalPlacesAvailable] = useState(
    initialPlacesView?.totalPlaces || initialLandingPlaces.length || 0
  );
  const [places, setPlaces] = useState(initialLandingPlaces);
  const [shouldFetchPlaces, setShouldFetchPlaces] = useState(false);
  const [placeFilters, setPlaceFilters] = useState(null);
  const [isUsingInitialLandingPlaces, setIsUsingInitialLandingPlaces] = useState(
    initialLandingPlaces.length > 0
  );

  const lastFetchRef = useRef(null);
  const didMountPlaceFiltersRef = useRef(false);
  const didInitZoomRef = useRef(false);
  // Track the last radius/search params we actually acted on. When the place
  // detail opens as an intercepted overlay the URL drops the /places query
  // params and re-adds them on close, which made these effects fire a spurious
  // completePlacesReset (a full refetch) on every back — a cold ~seconds fetch
  // on mobile that left the just-tapped pin unresponsive until it finished.
  const lastRadiusParamRef = useRef(_mr);
  const lastSearchParamRef = useRef(_searchPlace);

  function generateMarkers(_places) {
    if (Array.isArray(_places)) {
      // Fan co-located distinct places (same building → byte-identical coords)
      // onto a ~6m ring so each gets its own tappable pin/label. GEOMETRY only —
      // place lat/lng and list distances untouched, non-colliding pins unchanged,
      // clustering unaffected (spread is sub-10m, only visible at high zoom).
      // Mirrors the desktop builder via @/utils/placeMapMarkers (worklog-2026-07-27b).
      const colocationGroups = buildColocationGroups(_places);
      const colocationSeen = new Map();
      const newMarkers = _places.map((p, i) => {
        const coordKey = getColocatedCoordKey(p);
        const groupSize = coordKey ? colocationGroups.get(coordKey) || 1 : 1;
        let coordinates = [Number(p?.longitude), Number(p?.latitude)];
        if (coordKey && groupSize > 1) {
          const memberIndex = colocationSeen.get(coordKey) || 0;
          colocationSeen.set(coordKey, memberIndex + 1);
          coordinates = getSpreadCoordinates(p, groupSize, memberIndex);
        }
        return {
          type: "Feature",
          id: getPlaceResultId(p, i),
          index: i + 1,
          geometry: {
            type: "Point",
            coordinates,
          },
          properties: {
            name: p?.name || p?.address,
            type: p?.type,
            description: p?.city || p?.state || p?.country,
            averageRating: p?.averageRating,
            totalReviews: p?.totalReviews,
            numberOfVisitorReviews: p?.numberOfVisitorReviews,
            naverRating: p?.naverRating,
          },
        };
      });
      return newMarkers;
    }
  }

  const handleCreateSearchHistory = async (searchQuery) => {
    try {
      if (!searchQuery?.trim() || !user?.userId) return;
      const payload = {
        userId: user?.userId,
        search: searchQuery,
      };
      await createPlaceSearchHistory(payload);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Error saving search history",
        { variant: "error" }
      );
    }
  };

  const handlePlaceSorting = (type) => {
    setPlaceListLoader(true);
    if (type === "rating") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) => Number(b?.naverRating) - Number(a?.naverRating)
      );
      const newMarkers = generateMarkers(sortedPlaces);
      setMapMarkers(newMarkers);
      setPlaces(sortedPlaces);
    } else if (type === "distance") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) => Number(a?.distanceFromMe) - Number(b?.distanceFromMe)
      );
      const newMarkers = generateMarkers(sortedPlaces);
      setMapMarkers(newMarkers);
      setPlaces(sortedPlaces);
    } else if (type === "numOfReviews") {
      const unsortedPlaces = [...places];
      const sortedPlaces = unsortedPlaces?.toSorted(
        (a, b) =>
          Number(b?.numberOfVisitorReviews) - Number(a?.numberOfVisitorReviews)
      );
      const newMarkers = generateMarkers(sortedPlaces);
      setMapMarkers(newMarkers);
      setPlaces(sortedPlaces);
    }
    setPlaceListLoader(false);
  };

  const fetchPlaces = async () => {
    // Gate on the MAP CENTER, not the user's GPS. The query is built from
    // mapCenter (URL ?mc=), so on refresh/return we can load pins immediately
    // instead of blocking ~4s on the geolocation timeout — that dead window was
    // why tapping a store did nothing for ~5s. GPS is only used for the
    // "distance from me" label, which backfills once it resolves.
    if (!mapCenter) return;
    if (
      isUsingInitialLandingPlaces &&
      listPageNo === 1 &&
      !_searchPlace &&
      searchQuery === initialLandingSearchQuery
    ) {
      setPlaceListLoader(false);
      setInfiniteScrollLoader(false);
      return;
    }

    try {
      const explicitRadius = _mr && !isNaN(Number(_mr)) ? Number(_mr) : null;
      // "이 지역 N곳" should reflect what's VISIBLE on the map. Derive the search
      // radius from the current viewport (center → corner) so zooming in shrinks
      // the count/list to the visible places; bounds trim it to the exact box.
      const rawBoundsRadius =
        mapBounds && mapCenter
          ? calculateDistanceBetweenCoords(
              { latitude: mapCenter.lat, longitude: mapCenter.lng },
              {
                latitude: Number(mapBounds.north),
                longitude: Number(mapBounds.east),
              }
            )
          : null;
      // Snap the query inputs to a coarse grid so the SAME view produces the SAME
      // request every time → the backend Redis cache actually gets hit (revisits,
      // settle-jitter, tiny pans return instantly instead of a ~0.7s cold query).
      // Grid (~11m for coords, 100m for radius) is far finer than the 350m refetch
      // threshold, so it never changes what the user sees — it only stops a jittery
      // float from busting the cache key on every frame. (This was the pin-loading
      // regression: a continuous boundsRadius made almost every fetch a cache miss.)
      const snap = (n, step) =>
        Number.isFinite(Number(n)) ? Math.round(Number(n) / step) * step : n;
      const snappedCenter = mapCenter
        ? { lat: snap(mapCenter.lat, 1e-4), lng: snap(mapCenter.lng, 1e-4) }
        : mapCenter;
      const snappedBounds = mapBounds
        ? {
            north: snap(mapBounds.north, 1e-4),
            south: snap(mapBounds.south, 1e-4),
            east: snap(mapBounds.east, 1e-4),
            west: snap(mapBounds.west, 1e-4),
          }
        : mapBounds;
      const boundsRadius =
        Number.isFinite(rawBoundsRadius) && rawBoundsRadius > 0
          ? Math.max(100, Math.round(rawBoundsRadius / 100) * 100)
          : null;
      const payload = {
        mapCenter: snappedCenter,
        mapBounds: snappedBounds,
        mapRadius: boundsRadius ?? explicitRadius ?? 1000,
        searchQuery: searchQuery?.length > 0 ? searchQuery : null,
        filters:
          placeFilters && Object.keys(placeFilters)?.length > 0
            ? JSON.stringify(placeFilters)
            : null,
        page: Number(listPageNo),
      };

      const serializedPayload = JSON.stringify(payload);
      if (lastFetchRef.current === serializedPayload) return;
      lastFetchRef.current = serializedPayload;

      if (payload.searchQuery) handleCreateSearchHistory(payload.searchQuery);

      // base list for infinite-scroll pages (>1); page 1 starts fresh
      const basePlaces = listPageNo === 1 ? [] : places;
      const withDistance = (list) =>
        (list || []).map((p) => {
          // No GPS yet? Leave distance null (backfilled when it resolves) rather
          // than computing NaN. The list is already ordered by geoNear.
          const distanceFromMe = currentUserLocation
            ? calculateDistanceBetweenCoords(
                { latitude: Number(p?.latitude), longitude: Number(p?.longitude) },
                {
                  latitude: currentUserLocation.latitude,
                  longitude: currentUserLocation.longitude,
                }
              )
            : null;
          return { ...p, distance: distanceFromMe, distanceFromMe };
        });

      // SINGLE SOURCE: our own DB (getAllPlacesWithReviews) already contains the
      // 예전 외부 소스에서 들여온 장소들 — 오래전에 Place 컬렉션으로 합쳐졌다.
      // 따로 살아 있던 외부 조회는 중복이었고
      // used a different area rule (radius) than the hotple query (viewport
      // bounds), so it surfaced off-viewport places WITHOUT our thumbnails and
      // made "이 지역 N곳" jump (two mismatched counts summed, e.g. 17 → 3 on
      // refetch). Removed → one source, one area rule: the list matches the map
      // pins and thumbnails always attach.
      const hotpleResponse = await getAllPlacesWithReviews(payload).catch((e) => {
        console.warn("Hotple places request failed:", e);
        return null;
      });

      // A newer map move already superseded this fetch → drop the stale result
      // so it can't repaint the map with an out-of-date area.
      if (lastFetchRef.current !== serializedPayload) return;

      const total = getPlacesTotalCount(hotpleResponse);

      // 지역이 인식된 검색이면(예: "성수동 브런치") 지도를 그 동네로 옮긴다.
      const matchedArea = hotpleResponse?.area;
      if (matchedArea?.center && payload.page === 1) {
        setMapCenter({ lat: matchedArea.center.lat, lng: matchedArea.center.lng });
        setMapZoom(getZoomForAreaBounds(matchedArea.bbox));
      }

      // Map plots the full lightweight pin set (up to the nearest cap); the
      // list/sheet shows the hydrated rows. Falls back to the list rows when the
      // backend sent no pins (search / amenities path).
      const mapPins = Array.isArray(hotpleResponse?.pins)
        ? hotpleResponse.pins.map((p) => ({ ...p, source: "api" }))
        : [];
      const merged = mergePlacesByIdentifier(hotpleResponse?.places);
      const withDist = withDistance(merged);
      const nextPlaces =
        listPageNo === 1
          ? withDist
          : mergePlacesByIdentifier(basePlaces, withDist);

      // Draw the map PINS first (urgent) so they become clickable without waiting
      // for the list rows to render. The list + counts are a non-urgent transition
      // — React commits the pin update, then renders the list in the background.
      // Pins are the interactive layer users tap; the list can trail a beat.
      setMapMarkers(generateMarkers(mapPins.length ? mapPins : nextPlaces));
      const hotplePage = hotpleResponse?.page ?? payload.page;
      if (hotplePage) setApiPageNo(Number(hotplePage));
      startTransition(() => {
        setPlaces(nextPlaces);
        setTotalPlacesAvailable(total);
        if (payload.page === 1) setTotalResults(total);
        setHasMorePlaces(merged.length > 0 && nextPlaces.length < total);
      });
      setPlaceListLoader(false);
      setInfiniteScrollLoader(false);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
        { variant: "error" }
      );
    } finally {
      console.log("FETCHING PLACES REQUEST FINISHED");
      setPlaceListLoader(false);
      setInfiniteScrollLoader(false);
    }
  };

  const completePlacesReset = () => {
    setIsUsingInitialLandingPlaces(false);
    setInfiniteScrollLoader(true);
    setPlaceListLoader(true);
    setListPageNo(1);
    setApiPageNo(0);
    setHasMorePlaces(true);
    setPlaces([]);
    lastFetchRef.current = null;
    setShouldFetchPlaces(true);
  };

  // Refetch for map pans/zooms that does NOT blank the list first. Reconciling
  // the new results against the visible list by key means places that stay in
  // view (e.g. the one under the map center) keep their already-painted
  // thumbnail — clearing the list remounted every row, and iOS Safari would
  // not repaint the freshly-mounted <img>s inside the sheet's transform layer,
  // so images vanished after a zoom. Also avoids an empty-list flash.
  const refetchAreaKeepingList = () => {
    setIsUsingInitialLandingPlaces(false);
    setInfiniteScrollLoader(true);
    setListPageNo(1);
    setApiPageNo(0);
    setHasMorePlaces(true);
    lastFetchRef.current = null;
    setShouldFetchPlaces(true);
  };

  const handleSearchAroundMyLocation = () => {
    addQueryParam(
      "mc",
      `${currentUserLocation?.latitude},${currentUserLocation?.longitude}`
    );
    addQueryParam("mz", "15");
    addQueryParam("show-around-me", "yes");
    setMapCenter({
      lat: currentUserLocation?.latitude,
      lng: currentUserLocation?.longitude,
    });
    setSearchQuery("");
    removeQueryParam("search-place");
    setTimeout(() => completePlacesReset(), 1000);
  };

  useEffect(() => {
    if (!didMountPlaceFiltersRef.current) {
      didMountPlaceFiltersRef.current = true;
      if (isUsingInitialLandingPlaces) return;
    }

    if (
      isUsingInitialLandingPlaces &&
      listPageNo === 1 &&
      !_searchPlace &&
      searchQuery === initialLandingSearchQuery
    ) {
      return;
    }

    completePlacesReset();
  }, [placeFilters]);

  useEffect(() => {
    if (shouldFetchPlaces) {
      fetchPlaces();
      setShouldFetchPlaces(false);
    }
  }, [shouldFetchPlaces]);

  // GPS often resolves a few seconds after the pins already loaded (we no longer
  // wait for it). Backfill the "distance from me" on the rows that loaded
  // without it — no refetch, just fills the label in place.
  useEffect(() => {
    if (!currentUserLocation) return;
    setPlaces((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      if (prev.every((p) => p?.distanceFromMe != null)) return prev;
      return prev.map((p) => {
        if (p?.distanceFromMe != null) return p;
        const d = calculateDistanceBetweenCoords(
          { latitude: Number(p?.latitude), longitude: Number(p?.longitude) },
          {
            latitude: currentUserLocation.latitude,
            longitude: currentUserLocation.longitude,
          }
        );
        return { ...p, distance: d, distanceFromMe: d };
      });
    });
  }, [currentUserLocation]);

  useEffect(() => {
    if (listPageNo > 1 && listPageNo > apiPageNo) {
      setInfiniteScrollLoader(true);
      fetchPlaces();
    }
  }, [listPageNo]);

  useEffect(() => {
    const mc = _mc;
    const [mcLat, mcLng] = mc?.split(",") || [];
    if (mc && mcLat && mcLng && !isNaN(mcLat) && !isNaN(mcLng) && !mapCenter) {
      setMapCenter({ lat: Number(mcLat), lng: Number(mcLng) });
    } else if (!mapCenter && currentUserLocation) {
      setMapCenter({
        lat: currentUserLocation.latitude,
        lng: currentUserLocation.longitude,
      });
    }
  }, [_mc]);

  useEffect(() => {
    // Restore the zoom from the URL ONCE on mount (e.g. returning from a place
    // detail). The old `!mapZoom` guard skipped this whenever mapZoom already
    // had a default (initialPlacesView.mapZoom = 15), so the center restored
    // (mapCenter inits to null) but the zoom always snapped back to 15. Using a
    // one-shot ref lets the URL zoom win on mount without fighting user zooms
    // later in the session.
    if (didInitZoomRef.current) return;
    const mz = _mz;
    if (mz && !isNaN(mz)) {
      didInitZoomRef.current = true;
      setMapZoom(Number(mz));
    }
  }, [_mz]);

  useEffect(() => {
    const _rad = _mr;
    if (!_rad || isNaN(Number(_rad))) return; // param dropped (overlay URL) — ignore
    if (lastRadiusParamRef.current === _rad) return; // unchanged (returned from overlay) — no refetch
    lastRadiusParamRef.current = _rad;
    if (apiPageNo > 0) completePlacesReset();
  }, [_mr]);

  useEffect(() => {
    const _search = _searchPlace;
    if (!_search || _search.length === 0) return;
    if (lastSearchParamRef.current === _search) return;
    lastSearchParamRef.current = _search;
    if (apiPageNo > 0) {
      setSearchQuery(_search);
      completePlacesReset();
    }
  }, [_searchPlace]);

  // 옛 목록 화면은 지웠다(2026-08-13). 지도+시트가 그 일을 한다.
  if (screen === "map") {
    return (
      <MobilePlaceMapMaplibre
        loader={placeListLoader}
        places={places}
        addQueryParam={addQueryParam}
        mapCenter={mapCenter}
        mapZoom={mapZoom}
        mapMarkers={mapMarkers}
        setMapCenter={setMapCenter}
        setMapZoom={setMapZoom}
        setMapBounds={setMapBounds}
        fetchPlaces={fetchPlaces}
        listPageNo={listPageNo}
        setListPageNo={setListPageNo}
        apiPageNo={apiPageNo}
        switchScreen={switchScreen}
        infiniteScrollLoader={infiniteScrollLoader}
        searchAreaAgain={refetchAreaKeepingList}
        currentUserLocation={currentUserLocation}
        totalPlacesAvailable={totalPlacesAvailable}
        openPlaceAddModal={openPlaceAddModal}
        handlePlaceSorting={handlePlaceSorting}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearchAroundMyLocation={handleSearchAroundMyLocation}
        completePlacesReset={completePlacesReset}
        placeFilters={placeFilters}
        setPlaceFilters={setPlaceFilters}
        removeQueryParam={removeQueryParam}
      />
    );
  }

  return null;
};

export default MobileSecondaryScreen;
